// web-commands.ts — the PER-APP half of the web shape: maps oriora_garage's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (compute_signals) is the app's
// "paid draw" computed in signals.rs — on web it returns a minimal shell for now; a faithful web
// build reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore, Row } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import type { Vehicle, Fillup, Service } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const VEHICLES = "vehicles";
const FILLUPS = "fillups";
const SERVICES = "services";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function num(v: unknown, dflt = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : Number(v ?? dflt) || dflt;
}
function normUnit(raw: unknown, dflt: string, a: string, b: string): string {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === a.toLowerCase()) return a;
  if (s === b.toLowerCase()) return b;
  return dflt;
}
function normKind(raw: unknown): string {
  return String(raw ?? "").trim().toLowerCase().replace(/\s+/g, "-");
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Vehicles ──
    list_vehicles: () => {
      const rows = db.all(VEHICLES) as Array<{ created_at?: string }>;
      rows.sort((a, b) => String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")));
      return rows;
    },
    get_vehicle: ({ id }) => db.get(VEHICLES, String(id)),
    create_vehicle: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        name: String(i.name ?? ""),
        make: String(i.make ?? ""),
        model: String(i.model ?? ""),
        year: num(i.year, 0),
        plate: String(i.plate ?? ""),
        distance_unit: normUnit(i.distance_unit, "mi", "mi", "km"),
        volume_unit: normUnit(i.volume_unit, "gal", "gal", "L"),
        service_interval_km: Math.max(0, num(i.service_interval_km, 0)),
        service_interval_days: Math.max(0, num(i.service_interval_days, 0)),
        created_at: now,
        updated_at: now,
      };
      return db.insert(VEHICLES, row);
    },
    update_vehicle: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const next: Record<string, unknown> = { updated_at: nowIso() };
      for (const k of ["name", "make", "model", "year", "plate"]) if (k in p) next[k] = p[k];
      if ("distance_unit" in p) next.distance_unit = normUnit(p.distance_unit, "mi", "mi", "km");
      if ("volume_unit" in p) next.volume_unit = normUnit(p.volume_unit, "gal", "gal", "L");
      if ("service_interval_km" in p) next.service_interval_km = Math.max(0, num(p.service_interval_km, 0));
      if ("service_interval_days" in p) next.service_interval_days = Math.max(0, num(p.service_interval_days, 0));
      db.update(VEHICLES, String(id), next);
      return null;
    },
    delete_vehicle: ({ id }) => {
      const vid = String(id);
      // Mirror ON DELETE CASCADE: drop the vehicle's fill-ups + services too.
      for (const f of db.all(FILLUPS)) if ((f as { vehicle_id?: string }).vehicle_id === vid) db.remove(FILLUPS, f.id);
      for (const s of db.all(SERVICES)) if ((s as { vehicle_id?: string }).vehicle_id === vid) db.remove(SERVICES, s.id);
      db.remove(VEHICLES, vid);
      return null;
    },

    // ── Fill-ups ──
    list_fillups: ({ vehicleId, limit }) => {
      let rows = db.all(FILLUPS) as Array<{ vehicle_id?: string; odometer?: number; ts?: string }>;
      rows = rows.filter((r) => r.vehicle_id === String(vehicleId));
      rows.sort((a, b) => (num(b.odometer) - num(a.odometer)) || String(b.ts ?? "").localeCompare(String(a.ts ?? "")));
      const lim = typeof limit === "number" ? limit : Number(limit ?? 50000);
      return rows.slice(0, lim);
    },
    get_fillup: ({ id }) => db.get(FILLUPS, String(id)),
    create_fillup: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        vehicle_id: String(i.vehicle_id ?? ""),
        ts: String(i.ts ?? now),
        odometer: num(i.odometer),
        volume: num(i.volume),
        cost: num(i.cost),
        is_full_tank: i.is_full_tank === undefined ? true : Boolean(i.is_full_tank),
        note: String(i.note ?? ""),
        photo: typeof i.photo === "string" ? i.photo : "",
        created_at: now,
        updated_at: now,
      };
      return db.insert(FILLUPS, row);
    },
    update_fillup: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const next: Record<string, unknown> = { updated_at: nowIso() };
      for (const k of ["ts", "odometer", "volume", "cost", "is_full_tank", "note", "photo"]) if (k in p) next[k] = p[k];
      db.update(FILLUPS, String(id), next);
      return null;
    },
    delete_fillup: ({ id }) => {
      db.remove(FILLUPS, String(id));
      return null;
    },

    // ── Services ──
    list_services: ({ vehicleId, limit }) => {
      let rows = db.all(SERVICES) as Array<{ vehicle_id?: string; ts?: string }>;
      rows = rows.filter((r) => r.vehicle_id === String(vehicleId));
      rows.sort((a, b) => String(b.ts ?? "").localeCompare(String(a.ts ?? "")));
      const lim = typeof limit === "number" ? limit : Number(limit ?? 50000);
      return rows.slice(0, lim);
    },
    get_service: ({ id }) => db.get(SERVICES, String(id)),
    create_service: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        vehicle_id: String(i.vehicle_id ?? ""),
        ts: String(i.ts ?? now),
        odometer: num(i.odometer),
        kind: normKind(i.kind),
        cost: num(i.cost),
        note: String(i.note ?? ""),
        created_at: now,
        updated_at: now,
      };
      return db.insert(SERVICES, row);
    },
    update_service: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const next: Record<string, unknown> = { updated_at: nowIso() };
      for (const k of ["ts", "odometer", "cost", "note"]) if (k in p) next[k] = p[k];
      if ("kind" in p) next.kind = normKind(p.kind);
      db.update(SERVICES, String(id), next);
      return null;
    },
    delete_service: ({ id }) => {
      db.remove(SERVICES, String(id));
      return null;
    },

    // ── Derived — native computes rich signals in signals.rs. Web shell = valid-but-empty.
    // Derived — Rust in signals.rs, faithfully ported for web in signals-web.ts.
    // This used to be a valid-but-EMPTY shell: the whole InsightsPanel (economy, cost/distance,
    // spend, projection) rendered zeros and the billed narration got an empty logbook.
    compute_signals: ({ vehicleId, today }) => {
      const v = db.get(VEHICLES, String(vehicleId)) as unknown as Vehicle | null;
      if (!v) return null;
      const mine = (rows: Row[]) => rows.filter((r) => String((r as { vehicle_id?: string }).vehicle_id ?? "") === String(vehicleId));
      return computeSignalsWeb(
        v,
        mine(db.all(FILLUPS)) as unknown as Fillup[],
        mine(db.all(SERVICES)) as unknown as Service[],
        String(today ?? new Date().toISOString().slice(0, 10)),
      );
    },
  };
}
